import { motion, useSpring } from 'framer-motion'
import { useReducedMotion } from '@/lib/useReducedMotion'
import { identity } from '@/content/portfolio'
import { cn } from '@/lib/utils'

function DotPortrait({ className }: { className: string }) {
  const reducedMotion = useReducedMotion()
  const x = useSpring(0, { stiffness: 180, damping: 24 })
  const y = useSpring(0, { stiffness: 180, damping: 24 })

  return (
    <div
      className={cn('relative isolate overflow-hidden p-6 lg:p-9', className)}
      onPointerMove={event => {
        if (reducedMotion || event.pointerType !== 'mouse' || !window.matchMedia('(min-width: 768px) and (hover: hover)').matches) return
        const bounds = event.currentTarget.getBoundingClientRect()
        x.set(((event.clientX - bounds.left) / bounds.width - 0.5) * 10)
        y.set(((event.clientY - bounds.top) / bounds.height - 0.5) * 10)
      }}
      onPointerLeave={() => { x.set(0); y.set(0) }}
    >
      <motion.div
        aria-hidden="true"
        className="pointer-events-none absolute inset-2 -z-10"
        style={{
          x: reducedMotion ? 0 : x,
          y: reducedMotion ? 0 : y,
          backgroundImage: 'radial-gradient(circle, rgb(var(--text-secondary) / 0.4) 1.3px, transparent 1.5px)',
          backgroundSize: '18px 18px',
        }}
      >
        <div className="absolute right-0 top-0 h-28 w-28" style={{
          backgroundImage: 'radial-gradient(circle, rgb(var(--accent)) 1.5px, transparent 1.7px)',
          backgroundSize: '18px 18px',
          maskImage: 'linear-gradient(225deg, black, transparent 80%)',
        }} />
      </motion.div>
      <img src="/headshot.webp" alt="Van (Will) Tran" width={800} height={800} className="aspect-square w-full" />
    </div>
  )
}

export function Summary() {
  const reducedMotion = useReducedMotion()
  return (
    <section id="about" className="border-t border-vibe-border bg-vibe-black py-12 lg:py-20">
      <div className="mx-auto max-w-6xl px-5 lg:px-8">
        <div className="grid items-center gap-10 md:grid-cols-[1.3fr_1fr] lg:gap-24">
          <div>
            <div className="section-label mb-5">About · The person behind the work</div>
            <motion.h1 initial={reducedMotion ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }} className="text-5xl font-medium tracking-tight text-text-heading lg:text-7xl">
              Hey, I’m Van <span className="whitespace-nowrap text-accent-primary">( Will )</span> Tran.
            </motion.h1>
            <p className="mt-6 font-mono text-sm leading-relaxed text-text-primary">Product manager, facilitator,<br />and systems thinker.</p>
            <DotPortrait className="my-6 w-64 max-w-full md:hidden" />
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-text-secondary">I bridge technology, people, and business. I enjoy untangling difficult problems, making ideas tangible, and helping teams build things that matter.</p>
            <p className="mt-4 max-w-lg leading-relaxed text-text-secondary">Funny, techy, and empathetic. I care about business outcomes and whether the team enjoys getting there.</p>
            <div className="mt-7 flex flex-wrap gap-x-6 gap-y-3 text-sm">
              <a href={`mailto:${identity.email}`} className="text-accent-primary underline underline-offset-4">Shoot me a message ↗</a>
              <span className="text-text-secondary">Ho Chi Minh City, Vietnam</span>
            </div>
          </div>
          <motion.figure initial={reducedMotion ? false : { opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} className="hidden md:block">
            <DotPortrait className="w-full" />
            <figcaption className="mt-4 flex justify-between font-mono text-xs text-text-secondary"><span>Van to some. Will to others.</span><span>Same person.</span></figcaption>
          </motion.figure>
        </div>
        <nav aria-label="About sections" className="mt-12 flex flex-wrap gap-x-7 gap-y-4 border-t border-vibe-border pt-6 font-mono text-xs text-text-secondary lg:mt-16">
          <a href="#beliefs" className="hover:text-accent-primary">01 / Values</a>
          <a href="#experience" className="hover:text-accent-primary">02 / My path</a>
          <a href="#references" className="hover:text-accent-primary">03 / People</a>
          <a href="#learning" className="hover:text-accent-primary">04 / Curiosity</a>
          <a href="#teaching" className="hover:text-accent-primary">05 / Community</a>
        </nav>
      </div>
    </section>
  )
}
